import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { nbPerPageLabel } from 'referencial/constants/ReferencialConstants'
import { groupBy, isNil, isUndefined, orderBy, round, uniqBy } from 'lodash'
import { getYear } from 'utils/DateUtil'
import { getLabelTruncate } from 'utils/StoreUtils'
import { calculateAverage, calculateGeometricAverage, calculateRangeInterquartile, calculateStandardDeviation, filterDetection, filterQuantification, filterResult, filterValid, searchP90Value, searchMinValue, searchMaxValue } from 'utils/AnalyseUtils'
import { substringText } from 'utils/StringUtil'
import i18n from 'simple-react-i18n'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import useListIndexed from 'utils/customHook/useListIndexed'
import TooltipAnalyse from 'quality/components/qualityComponents/TooltipAnalyse'
import DtoQualityThresholds from 'quality/dto/QualityThreshold/DtoQualityThresholds'
import { useRegroupPiezoDatas } from '../hook/usePiezoDatas'
import { useRegroupHydroDatas } from '../hook/useHydroDatas'
import { useRegroupPluvioDatas } from '../hook/usePluvioDatas'
import { exportModelFile, getModelFileType } from 'utils/ExportDataUtil'
import PcMonitoringRegroupExportModal from '../export/PcMonitoringRegroupExportModal'
import { getAnalyseByGroup, getFluxByGroup } from 'quality/utils/PcMonitoringUtil'
import useBoolean from 'utils/customHook/useBoolean'
import { ParameterGraphModal } from 'quality/components/qualityComponents/ParameterGraph'
import { getHardPiezoDataTypes } from 'utils/PiezometryUtils'
import DtoQualityThreshold from 'quality/dto/QualityThreshold/DtoQualityThreshold'
import moment from 'moment'
import { HYDRO, PIEZO, PLUVIO } from 'quality/constants/ChartConstant'
import { ALIGN, NewTable } from 'components/datatable/NewTable'
import { PASTEL_COLOR } from 'utils/constants/ColorTheme'

const GraphModal = ({
    analysis = [],
    thresholds,

    hydroDatas = [],
    piezoDatas = [],
    pluvioDatas = [],

    horizon = 'year',
    regrouping = 'max',

    graphArgument = {},
    groupEquivalences = false,

    isGraphOpen = false,
    closeGraph = () => { },
}) => {
    const {
        settings,
        piezometers,
        piezometryDataTypes,
        hydrometricStations,
        pluviometers,
        pluviometryDataTypes,
    } = useSelector(store => ({
        settings: store.AdministrationReducer.settings,
        piezometers: store.PiezometryReducer.piezometersLight,
        piezometryDataTypes: store.PiezometryReducer.piezometryDataTypes,
        hydrometricStations: store.HydrometryReducer.hydrometricStations,
        pluviometers: store.PluviometryReducer.pluviometers,
        pluviometryDataTypes: store.PluviometryReducer.pluviometryDataTypes,
    }), shallowEqual)

    const piezometersIndex = useListIndexed(piezometers, 'id')
    const hydrometricStationsIndex = useListIndexed(hydrometricStations, 'id')
    const pluviometersIndex = useListIndexed(pluviometers, 'id')

    const filteredAnalysis = useMemo(() => {
        const {
            parameter,
            units = [],
            supports = [],
            fractions = [],
        } = graphArgument
        if (!parameter) {
            return []
        }
        return analysis
            .filter(a => a.parameter === parameter)
            .filter(a => !units.length ? isUndefined(a.unit) : units.includes(a.unit))
            .filter(a => !supports.length ? isUndefined(a.support) : supports.includes(a.support))
            .filter(a => !fractions.length ? isUndefined(a.fraction) : fractions.includes(a.fraction))
    }, [analysis, graphArgument])

    const formattedAnalysis = useMemo(() => {
        const analysisGroupByYear = groupBy(filteredAnalysis, a => horizon === 'year' ? getYear(a.sampleDate) : 'history')
        return Object.keys(analysisGroupByYear).map(year => getAnalyseByGroup(analysisGroupByYear[year], regrouping, settings))
    }, [filteredAnalysis, horizon, regrouping, settings])

    const dataTypePiezo = useMemo(() => [
        { id: 'piezometryChronic', label: i18n.chronic },
        ...getHardPiezoDataTypes(),
        ...piezometryDataTypes,
    ], [piezometryDataTypes])

    const piezoDatasFormatted = useMemo(() => {
        return piezoDatas.map(piezoData => {
            const piezo = piezometersIndex[piezoData.id]
            if (!piezo) {
                return null
            }
            return {
                name: piezo.name,
                unit: dataTypePiezo.find(t => t.id === piezoData.type)?.label || '',
                showSymbol: false,
                dataType: PIEZO,
                dataList: piezoData.measures.map(m => ({
                    date: m.date,
                    value: m.NGF ?? m.value,
                })),
            }
        }).filter(p => p?.dataList.length)
    }, [dataTypePiezo, piezoDatas, piezometersIndex])

    const hydroDatasFormatted = useMemo(() => {
        return hydroDatas.map(hydroData => {
            const hydro = hydrometricStationsIndex[hydroData.id]
            if (!hydro) {
                return null
            }
            return {
                name: hydro.name,
                unit: hydroData.type,
                showSymbol: false,
                dataType: HYDRO,
                dataList: hydroData.measures,
            }
        }).filter(h => h?.dataList.length)
    }, [hydroDatas, hydrometricStationsIndex])

    const pluvioDatasFormatted = useMemo(() => {
        return pluvioDatas.map(pluvioData => {
            const pluvio = pluviometersIndex[pluvioData.id]
            if (!pluvio) {
                return null
            }
            return {
                name: pluvio.name,
                unit: pluviometryDataTypes.find(t => t.id === pluvioData.type)?.label || '',
                dataType: PLUVIO,
                dataList: pluvioData.measures.map(m => ({ value: m.value, date: moment(m.date).add(pluvioData.offset ?? 0, 'day').valueOf() })),
                type: 'bar',
            }
        }).filter(p => p?.dataList.length)
    }, [pluvioDatas, pluviometersIndex, pluviometryDataTypes])

    return (
        <ParameterGraphModal
            isOpen={isGraphOpen}
            closeGraph={closeGraph}
            analysis={formattedAnalysis}
            thresholds={thresholds}
            additionalData={[...piezoDatasFormatted, ...hydroDatasFormatted, ...pluvioDatasFormatted]}

            graphOptions={{
                regroupAxis: groupEquivalences,
            }}
        />
    )
}

GraphModal.propTypes = {
    analysis: PropTypes.arrayOf(PropTypes.shape({/* ...DtoAnalysisLight, ...calculateThresholdsResult */ })),
    thresholds: PropTypes.arrayOf(PropTypes.instanceOf(DtoQualityThreshold)),

    hydroDatas: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        type: PropTypes.string,
        calculateFlux: PropTypes.bool,
        coeffFlux: PropTypes.number,
        measures: PropTypes.arrayOf(PropTypes.shape({})),
    })),
    piezoDatas: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        type: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
        ]),
        measures: PropTypes.arrayOf(PropTypes.shape({})),
    })),
    pluvioDatas: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        type: PropTypes.number,
        offset: PropTypes.number,
        cumul: PropTypes.number,
        measures: PropTypes.arrayOf(PropTypes.shape({})),
    })),

    horizon: PropTypes.oneOf(['operations', 'year', 'historic']),
    regrouping: PropTypes.oneOf(['max', 'moyenne', 'percentile']),

    groupEquivalences: PropTypes.bool,
    graphArgument: PropTypes.shape({
        parameter: PropTypes.string,
        units: PropTypes.arrayOf(PropTypes.string),
        supports: PropTypes.arrayOf(PropTypes.number),
        fractions: PropTypes.arrayOf(PropTypes.string),
    }),

    isGraphOpen: PropTypes.bool,
    closeGraph: PropTypes.func,
}

const ResultTable = ({
    analysis = [],
    threshold = {},

    hydroDatas = [],
    piezoDatas = [],
    pluvioDatas = [],

    horizon = 'year',
    regrouping = 'max',

    groupEquivalences = false,
    displayAdvancedStatistics = false,

    openGraph = () => {},
}) => {
    const {
        parameters,
        units,
        fractions,
        supports,
        settings,
        parameterGroupUsage,
        thresholds,
    } = useSelector(store => ({
        parameters: store.ParameterReducer.parameters,
        units: store.UnitReducer.units,
        fractions: store.FractionReducer.fractions,
        supports: store.SupportReducer.supports,
        settings: store.AdministrationReducer.settings,
        parameterGroupUsage: store.ParameterReducer.parameterGroupUsage,
        thresholds: store.QualityReducer.thresholds,
    }), shallowEqual)

    const parametersIndex = useListIndexed(parameters, 'code')
    const unitsIndex = useListIndexed(units, 'code')
    const fractionsIndex = useListIndexed(fractions, 'id')
    const supportsIndex = useListIndexed(supports, 'id')

    const dates = useMemo(() => {
        if (horizon === 'year') {
            return orderBy(uniqBy(analysis, a => getYear(a.sampleDate)), 'sampleDate').map(a => `${getYear(a.sampleDate)}`)
        }
        return ['history']
    }, [horizon, analysis])

    const shouldCalculateFlow = hydroDatas.some(({ calculateFlux }) => calculateFlux)

    const piezoDatasFormatted = useRegroupPiezoDatas(piezoDatas, dates, regrouping)
    const {
        hydroDatasFormatted,
        hydroFluxData,
    } = useRegroupHydroDatas(hydroDatas, dates, regrouping)
    const pluvioDatasFormatted = useRegroupPluvioDatas(pluvioDatas, dates, regrouping)

    const formatUnitLabel = unit => {
        if (!unit) {
            return ''
        }
        if (shouldCalculateFlow) {
            return `${unit.symbol} * m3/s`
        }
        return `${unit.symbol} [${unit.code}]`
    }

    const data = useMemo(() => {
        const analysisGroupByParameter = groupBy(analysis, ({ parameter, unit, fraction, support }) => groupEquivalences ? parameter : `${parameter}#:#${unit}#:#${fraction}#:#${support}`)
        return Object.keys(analysisGroupByParameter).map(paramKey => {
            const analysisGroup = analysisGroupByParameter[paramKey].map(a => {
                const dateFormat = horizon === 'year' ? getYear(a.sampleDate) : 'history'
                const hydroValue = hydroFluxData.reduce((sumFlux, { measures, coeffFlux = 1 }) => {
                    const m = measures[dateFormat]
                    return m ? sumFlux + m.value * coeffFlux : sumFlux
                }, 0)
                const valueFlux = shouldCalculateFlow ? round(a.result * hydroValue, 5) : a.result
                return {
                    ...a,
                    valueFlux,
                }
            })

            const [parameterCode] = paramKey.split('#:#')
            const parameter = parametersIndex[parameterCode]
            const parameterLabel = parameter?.shortLabel || parameter?.name

            const analysisGroupByYear = groupBy(analysisGroup, a => horizon === 'year' ? getYear(a.sampleDate) : 'history')

            const valuesObj = Object.keys(analysisGroupByYear).reduce((acc, year) => {
                const analyse = shouldCalculateFlow ? getFluxByGroup(analysisGroupByYear[year], regrouping) : getAnalyseByGroup(analysisGroupByYear[year], regrouping, settings)
                if (isUndefined(analyse)) {
                    return acc
                }
                acc[year] = {
                    align: ALIGN.RIGHT,
                    value: shouldCalculateFlow ? analyse.valueFlux : analyse.value,
                    sortValue: analyse.result,
                    color: isNil(analyse.index) && isNil(analyse.result) ? '#9e9e9e' : analyse.color,
                    tooltip: (
                        <TooltipAnalyse
                            title={parameter?.shortLabelWithCode || ''}
                            analyse={analyse}
                            threshold={analyse.threshold}
                        />
                    ),
                    result: analyse.value, // use for substance
                    parameter: analyse.parameter, // use for substance
                    thresholdValue: analyse.threshold?.[`threshold${analyse.index}`], // used for substance
                    index: analyse.index,
                }
                return acc
            }, {})

            const unitCodes = uniqBy(analysisGroup, 'unit').map(a => a.unit).filter(u => !isUndefined(u))
            const labelUnits = unitCodes.map(code => formatUnitLabel(unitsIndex[code]) || `<${code}>`).join(', ')

            const fractionCodes = uniqBy(analysisGroup, 'fraction').map(a => a.fraction).filter(u => !isUndefined(u))
            const labelFractions = fractionCodes.map(code => fractionsIndex[code]?.labelWithCode || `<${code}>`).join(', ')
            const labelTruncateFractions = fractionCodes.map(code => {
                const fration = fractionsIndex[code]
                return getLabelTruncate(fration?.name, fration?.code, 15)
            }).join(', ')

            const supportCodes = uniqBy(analysisGroup, 'support').map(a => a.support).filter(u => !isUndefined(u))
            const labelSupports = supportCodes.map(code => supportsIndex[code]?.labelWithCode || `<${code}>`).join(', ')
            const labelTruncateSupports = supportCodes.map(code => {
                const support = supportsIndex[code]
                return getLabelTruncate(support?.name, support?.code, 15)
            }).join(', ')

            const group = parameterGroupUsage.find(p => p.parameter === parameterCode)

            const validAnalysis = filterValid(filterResult(analysisGroup))

            const p90 = searchP90Value(validAnalysis)
            const min = searchMinValue(validAnalysis)
            const max = searchMaxValue(validAnalysis)
            const average = calculateAverage(validAnalysis, settings)
            const geometricAverage = calculateGeometricAverage(validAnalysis)
            const rangeInterquartile = calculateRangeInterquartile(validAnalysis)
            const standardDeviation = calculateStandardDeviation(validAnalysis)

            const nbAnalysis = validAnalysis.length
            const nbQuantification = filterQuantification(validAnalysis).length
            const nbDetection = filterDetection(validAnalysis).length
            const nbOverrun = validAnalysis.filter(a => a.index > 0).length

            const overrunTx = nbAnalysis ? `${round(nbOverrun * 100 / nbAnalysis, 2)} %` : '0%'
            const quantTx = nbAnalysis ? `${round(nbQuantification * 100 / nbAnalysis, 3)} %` : '0%'
            const detectTx = nbAnalysis ? `${round(nbDetection * 100 / nbAnalysis, 3)} %` : '0%'

            return {
                ...valuesObj,
                parameter: {
                    value: shouldCalculateFlow ? `${i18n.fluxOf} ${parameterLabel}` : getLabelTruncate(parameterLabel, parameterCode, 20),
                    code: parameterCode,
                    tooltip: shouldCalculateFlow ? `${i18n.fluxOf} ${parameter?.name || ''}` : parameter?.name,
                },
                group: {
                    value: getLabelTruncate(group?.groupName, group?.groupCode, 15),
                    tooltip: group?.labelWithCode,
                },
                unit: {
                    value: substringText(labelUnits, 26),
                    codes: unitCodes,
                    tooltip: labelUnits,
                },
                fraction: {
                    value: substringText(labelTruncateFractions, 26),
                    codes: fractionCodes,
                    tooltip: labelFractions,
                },
                support: {
                    value: substringText(labelTruncateSupports, 26),
                    codes: supportCodes,
                    tooltip: labelSupports,
                },
                min: { value: min, color: '#9e9e9e', align: ALIGN.RIGHT },
                averageShort: { value: average, color: '#9e9e9e', align: ALIGN.RIGHT },
                max: { value: max, color: '#9e9e9e', align: ALIGN.RIGHT },
                percentile90: { value: p90, color: '#9e9e9e', align: ALIGN.RIGHT },
                geometricAverageShort: { value: geometricAverage, color: '#9e9e9e', align: ALIGN.RIGHT },
                rangeInterquartile: { value: rangeInterquartile, color: '#9e9e9e', align: ALIGN.RIGHT },
                standardDeviation: { value: standardDeviation, color: '#9e9e9e', align: ALIGN.RIGHT },
                overrunNb: { value: nbOverrun, color: '#9e9e9e', align: ALIGN.RIGHT },
                overrunTx: { value: overrunTx, color: '#9e9e9e', align: ALIGN.RIGHT },
                nbAnalysis: { value: nbAnalysis, color: '#9e9e9e', align: ALIGN.RIGHT },
                nbQuant: { value: nbQuantification, color: '#9e9e9e', align: ALIGN.RIGHT },
                nbDetect: { value: nbDetection, color: '#9e9e9e', align: ALIGN.RIGHT },
                quantTx: { value: quantTx, color: '#9e9e9e', align: ALIGN.RIGHT },
                detectTx: { value: detectTx, color: '#9e9e9e', align: ALIGN.RIGHT },
            }
        })
    }, [analysis, hydroFluxData, fractionsIndex, groupEquivalences, horizon, parameterGroupUsage, parametersIndex, regrouping, settings, shouldCalculateFlow, supportsIndex, unitsIndex])

    const orderedData = useMemo(() => {
        return orderBy(data, d => d.parameter.code)
    }, [data])

    const substances = useMemo(() => {
        if (!threshold) {
            return []
        }
        const thresholdFound = thresholds.find(t => `${t.code}` === threshold.thresholdCode)

        const analysisValues = dates.reduce((acc, key) => {
            const keyData = orderedData.map(d => d[key]).filter(d => !isNil(d?.index) && d?.index !== 0)
            const [date] = key.split('#:#')
            const overrunedParameters = keyData.map((d, i) => {
                return (
                    <span key={i}>
                        {`\u2022 ${parametersIndex[d.parameter]?.displayName}: ${d.result} - ${i18n.threshold}(${d.thresholdValue ?? '?'})`}
                        <br />
                    </span>
                )
            })
            acc[key] = {
                value: keyData.length,
                date,
                align: ALIGN.RIGHT,
                color: overrunedParameters.length > 0 ? PASTEL_COLOR.red : '#9e9e9e',
                tooltip: (
                    <div>
                        <span>
                            {
                                overrunedParameters.length > 0 ? (
                                    <>
                                        {`${i18n.overrunedParameters}: `}
                                        <br />
                                        {overrunedParameters}
                                    </>
                                ) : (
                                    <>
                                        {i18n.noOverrun}
                                        <br />
                                    </>
                                )
                            }
                        </span>
                    </div>
                ),
            }
            return acc
        }, {})
        const values = dates.map(key => analysisValues[key].value)
        const overrunNb = values.filter(v => v !== 0).length

        const overrunTx = values.length ? `${round(overrunNb * 100 / values.length, 2)} %` : '0%'
        return [{
            parameter: { value: thresholdFound?.name, color: '#9e9e9e' },
            group: { value: '', color: '#9e9e9e' },
            unit: { value: '', color: '#9e9e9e' },
            support: { value: '', color: '#9e9e9e' },
            fraction: { value: '', color: '#9e9e9e' },
            ...analysisValues,
            overrunTx: {
                value: overrunTx,
                color: '#9e9e9e',
                align: ALIGN.RIGHT,
            },
            overrunNb: {
                value: overrunNb,
                color: '#9e9e9e',
                align: ALIGN.RIGHT,
            },
        }]
    }, [dates, orderedData, parametersIndex, threshold, thresholds])

    const advancedStatisticsHeaders = displayAdvancedStatistics ? ['rangeInterquartile', 'standardDeviation', 'overrunNb', 'overrunTx'] : []
    const headers = [
        { key: 'group', sticky: ALIGN.LEFT, width: 200 },
        { key: 'parameter', sticky: ALIGN.LEFT, width: 150 },
        { key: 'unit', sticky: ALIGN.LEFT, width: 150 },
        { key: 'support', sticky: ALIGN.LEFT, width: 150 },
        { key: 'fraction', sticky: ALIGN.LEFT, width: 150 },
        ...dates,
        'min', 'averageShort', 'max', 'percentile90', 'geometricAverageShort',
        ...advancedStatisticsHeaders,
        'nbDetect', 'nbQuant', 'nbAnalysis', 'detectTx', 'quantTx',
    ]

    return (
        <NewTable
            headers={headers}
            rows={[
                ...substances,
                ...piezoDatasFormatted,
                ...pluvioDatasFormatted,
                ...hydroDatasFormatted,
                ...orderedData,
            ]}
            rowsPerPageOptions={nbPerPageLabel}
            onClickRow={({ parameter, unit, support, fraction }) => {
                if (isUndefined(parameter.code)) {
                    return
                }
                openGraph({
                    parameter: parameter.code,
                    units: unit.codes,
                    supports: support.codes,
                    fractions: fraction.codes,
                })
            }}
        />
    )
}

ResultTable.propTypes = {
    analysis: PropTypes.arrayOf(PropTypes.shape({/* DtoAnalysisLight + calculateThresholdsResult */ })),
    threshold: PropTypes.instanceOf(DtoQualityThresholds),

    hydroDatas: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        type: PropTypes.string,
        calculateFlux: PropTypes.bool,
        coeffFlux: PropTypes.number,
        measures: PropTypes.arrayOf(PropTypes.shape({})),
    })),
    piezoDatas: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        type: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
        ]),
        measures: PropTypes.arrayOf(PropTypes.shape({})),
    })),
    pluvioDatas: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        type: PropTypes.number,
        offset: PropTypes.number,
        cumul: PropTypes.number,
        measures: PropTypes.arrayOf(PropTypes.shape({})),
    })),

    horizon: PropTypes.oneOf(['operations', 'year', 'historic']),
    regrouping: PropTypes.oneOf(['max', 'moyenne', 'percentile']),

    groupEquivalences: PropTypes.bool,
    displayAdvancedStatistics: PropTypes.bool,

    isExportModalOpen: PropTypes.bool,
    closeExportModal: PropTypes.func,

    openGraph: PropTypes.func,
}
// todo ajouter graph des données tableau, ajout piezo/hydro/pluvio pour max et p90 prend la mesure la plus proche et moyenne faire la moyenne
const PcMonitoringRegroupTable = ({
    analysis = [],
    threshold,

    hydroDatas = [],
    piezoDatas = [],
    pluvioDatas = [],

    horizon = 'year',
    regrouping = 'max',

    groupEquivalences = false,
    displayAdvancedStatistics = false,

    isExportModalOpen = false,
    closeExportModal = () => { },

    filter = {},
}) => {
    const dispatch = useDispatch()
    const {
        qualitometer,
        typeEnvironmentModels,
    } = useSelector(store => ({
        qualitometer: store.QualityReducer.qualitometer,
        typeEnvironmentModels: store.ExportReducer.typeEnvironmentModels,
    }), shallowEqual)

    const {
        value: isGraphOpen,
        setTrue: openGraph,
        setFalse: closeGraph,
    } = useBoolean(false)
    const [graphArgument, setGraphArgument] = useState({})

    const exportModel = useMemo(() => typeEnvironmentModels.map((model) => {
        const fileNameSplit = model.split('.')
        const name = fileNameSplit.slice(0, -1).join('')
        const ext = fileNameSplit[fileNameSplit.length - 1]
        return {
            name,
            formats: [{
                type: getModelFileType(ext),
                callback: () => exportModelFile({
                    stationId: qualitometer.id.toString(),
                    stationCode: qualitometer.code,
                    stationType: qualitometer.typeName,
                    environmentModels: typeEnvironmentModels,
                    filenameModelExport: model,
                    qualityFilter: {
                        stations: [qualitometer.id],
                        startDate: filter.startDate,
                        endDate: filter.endDate,
                        parameters: filter.parameters,
                        selectionCode: filter.selection,
                        groupCode: filter.group,
                        threshold: filter.threshold,

                        producers: filter.producers,
                        laboratories: filter.laboratories,
                        point: filter.point,
                        campaign: filter.campaign,
                        support: isNil(filter.support) ? undefined : `${filter.support}`,
                        fraction: filter.fraction,
                        status: filter.status,
                        qualification: filter.qualification,

                        quantificationControl: filter.quantificationControl,
                        detectionControl: filter.detectionControl,
                    },
                }),
            }],
        }
    }), [dispatch, qualitometer.code, qualitometer.id, qualitometer.typeName, typeEnvironmentModels, filter])

    return (
        <div style={{ paddingBottom: '100px' }}>
            <ResultTable
                analysis={analysis}
                threshold={threshold}

                hydroDatas={hydroDatas}
                piezoDatas={piezoDatas}
                pluvioDatas={pluvioDatas}

                horizon={horizon}
                regrouping={regrouping}

                groupEquivalences={groupEquivalences}
                displayAdvancedStatistics={displayAdvancedStatistics}

                openGraph={argument => {
                    setGraphArgument(argument)
                    openGraph()
                }}
            />
            <GraphModal
                isGraphOpen={isGraphOpen}
                closeGraph={closeGraph}

                analysis={analysis}
                thresholds={threshold?.thresholds}

                hydroDatas={hydroDatas}
                piezoDatas={piezoDatas}
                pluvioDatas={pluvioDatas}

                horizon={horizon}
                regrouping={regrouping}

                groupEquivalences={groupEquivalences}
                graphArgument={graphArgument}
            />
            <PcMonitoringRegroupExportModal
                analysis={analysis}
                threshold={threshold}

                hydroDatas={hydroDatas}
                piezoDatas={piezoDatas}
                pluvioDatas={pluvioDatas}

                horizon={horizon}
                regrouping={regrouping}

                groupEquivalences={groupEquivalences}
                exportModel={exportModel}

                isExportModalOpen={isExportModalOpen}
                closeExportModal={closeExportModal}
            />
        </div>
    )
}

PcMonitoringRegroupTable.propTypes = {
    analysis: PropTypes.arrayOf(PropTypes.shape({/* DtoAnalysisLight + calculateThresholdsResult */ })),
    threshold: PropTypes.instanceOf(DtoQualityThresholds),

    hydroDatas: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        type: PropTypes.string,
        calculateFlux: PropTypes.bool,
        coeffFlux: PropTypes.number,
        measures: PropTypes.arrayOf(PropTypes.shape({})),
    })),
    piezoDatas: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        type: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
        ]),
        measures: PropTypes.arrayOf(PropTypes.shape({})),
    })),
    pluvioDatas: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        type: PropTypes.number,
        offset: PropTypes.number,
        cumul: PropTypes.number,
        measures: PropTypes.arrayOf(PropTypes.shape({})),
    })),

    horizon: PropTypes.oneOf(['operations', 'year', 'historic']),
    regrouping: PropTypes.oneOf(['max', 'moyenne', 'percentile']),

    groupEquivalences: PropTypes.bool,
    displayAdvancedStatistics: PropTypes.bool,

    isExportModalOpen: PropTypes.bool,
    closeExportModal: PropTypes.func,

    filter: PropTypes.shape({}),
}

export default PcMonitoringRegroupTable